// @ts-check
/**
 * Typed client for the Service Desk API (tickets = `api_requirement`).
 *
 * TYPES ARE GENERATED, NOT HAND-WRITTEN. See `schema.d.ts` header for details.
 * Names match the Python ServiceDeskClient in kaironlibrary's /service-desk skill.
 * Every function returns the response body.
 */
import axios from 'axios'

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {import('./schema').operations} Operations */

/** @typedef {Schemas['Requirement']} Ticket */
/** @typedef {Schemas['PatchedRequirement']} TicketPatch */
/** @typedef {Schemas['RequirementType']} TicketType */
/** @typedef {Schemas['RequirementComment']} Comment */
/** @typedef {Schemas['RequirementMedia']} Attachment */
/** @typedef {Schemas['RequirementSourceEnum']} TicketSource */
/** @typedef {NonNullable<Operations['api_requirements_list']['parameters']['query']>} ListTicketsParams */

const TICKETS = '/api/requirements/'
const COMMENTS = '/api/requirement-comments/'
const ATTACHMENTS = '/api/requirement-media/'
const TICKET_TYPES = '/api/requirement-types/'

/**
 * GET /api/requirements/ — tickets visible to the caller. Pass `view: 'list'`
 * for the lighter grid rows.
 * @param {ListTicketsParams & Record<string, unknown>} [params]
 * @param {import('axios').AxiosRequestConfig} [config] e.g. `{ signal }` to cancel
 * @returns {Promise<Ticket[]>}
 */
export async function listTickets(params = {}, config = {}) {
  const { data } = await axios.get(TICKETS, { ...config, params })
  return data
}

/**
 * GET /api/requirements/{id}/
 * @param {number} id
 * @returns {Promise<Ticket>}
 */
export async function getTicket(id) {
  const { data } = await axios.get(`${TICKETS}${id}/`)
  return data
}

/**
 * POST /api/requirements/
 * @param {Partial<Ticket> & Record<string, unknown>} body
 * @returns {Promise<Ticket>}
 */
export async function createTicket(body) {
  const { data } = await axios.post(TICKETS, body)
  return data
}

/**
 * PATCH /api/requirements/{id}/ — edit fields (use setStatus for status).
 * @param {number} id
 * @param {TicketPatch & Record<string, unknown>} fields
 * @returns {Promise<Ticket>}
 */
export async function updateTicket(id, fields) {
  const { data } = await axios.patch(`${TICKETS}${id}/`, fields)
  return data
}

/**
 * DELETE /api/requirements/{id}/
 * @param {number} id
 * @returns {Promise<void>}
 */
export async function deleteTicket(id) {
  await axios.delete(`${TICKETS}${id}/`)
}

/**
 * PATCH /api/requirements/{id}/set-status/ — the only status write customers may use.
 * @param {number} id
 * @param {string} status
 * @returns {Promise<Ticket>}
 */
export async function setStatus(id, status) {
  const { data } = await axios.patch(`${TICKETS}${id}/set-status/`, { status })
  return data
}

/**
 * PATCH /api/requirements/{id}/assign/ — set or clear the owner.
 * @param {number} id
 * @param {number | null} ownerId
 * @returns {Promise<Ticket>}
 */
export async function setOwner(id, ownerId) {
  const { data } = await axios.patch(`${TICKETS}${id}/assign/`, { owner_id: ownerId })
  return data
}

/**
 * GET /api/requirements/{id}/assignees/ — users who may own this ticket.
 * @param {number} id
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function listAssignees(id) {
  const { data } = await axios.get(`${TICKETS}${id}/assignees/`)
  return data
}

/**
 * POST /api/requirements/reorder/
 * @param {Array<{ id: number, position: number }>} items
 * @returns {Promise<void>}
 */
export async function reorderTickets(items) {
  await axios.post(`${TICKETS}reorder/`, { items })
}

/**
 * GET /api/requirements/{id}/history/ — the ticket's activity log (staff only).
 * @param {number} id
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function getTicketHistory(id) {
  const { data } = await axios.get(`${TICKETS}${id}/history/`)
  return data
}

/**
 * GET /api/requirements/activity/ — activity feed across tickets (paginated).
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<{ count?: number, results?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>>}
 */
export async function listActivity(params = {}) {
  const { data } = await axios.get(`${TICKETS}activity/`, { params })
  return data
}

/**
 * GET /api/requirements/deadlines/ — what is due when (Hades board).
 * @returns {Promise<Record<string, unknown>>}
 */
export async function listDeadlines() {
  const { data } = await axios.get(`${TICKETS}deadlines/`)
  return data
}

/**
 * GET /api/requirement-types/
 * @returns {Promise<TicketType[]>}
 */
export async function listTicketTypes() {
  const { data } = await axios.get(TICKET_TYPES)
  return data
}

/**
 * GET /api/requirement-comments/?requirement={id} — plus `since`, `limit`, `offset`.
 * @param {number} ticketId
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<Comment[] | { count?: number, results?: Comment[] }>}
 */
export async function listComments(ticketId, params = {}) {
  const { data } = await axios.get(COMMENTS, { params: { requirement: ticketId, ...params } })
  return data
}

/**
 * POST /api/requirement-comments/
 * @param {Partial<Comment> & { requirement: number, body: string } & Record<string, unknown>} body
 * @returns {Promise<Comment>}
 */
export async function addComment(body) {
  const { data } = await axios.post(COMMENTS, body)
  return data
}

/**
 * GET /api/requirement-media/?requirement={id}
 * @param {number} ticketId
 * @returns {Promise<Attachment[] | { results?: Attachment[] }>}
 */
export async function listAttachments(ticketId) {
  const { data } = await axios.get(ATTACHMENTS, { params: { requirement: ticketId } })
  return data
}

/**
 * POST /api/requirement-media/ — record an already-uploaded file on a ticket.
 * @param {Partial<Attachment> & Record<string, unknown>} body
 * @returns {Promise<Attachment>}
 */
export async function addAttachment(body) {
  const { data } = await axios.post(ATTACHMENTS, body)
  return data
}

/**
 * GET /api/requirement-media/{id}/download/ — a short-lived URL for the file.
 * @param {number} id
 * @param {{ inline?: boolean }} [params]
 * @returns {Promise<{ url?: string } & Record<string, unknown>>}
 */
export async function getAttachmentDownload(id, params = {}) {
  const { data } = await axios.get(`${ATTACHMENTS}${id}/download/`, { params })
  return data
}

/**
 * DELETE /api/requirement-media/{id}/
 * @param {number} id
 * @returns {Promise<void>}
 */
export async function deleteAttachment(id) {
  await axios.delete(`${ATTACHMENTS}${id}/`)
}
